import React, { useState } from "react";
import {
  Cloud,
  Beaker,
  Database,
  Server,
  Code,
  ArrowRight,
  BookOpen,
  ExternalLink,
  Menu,
  X,
  FlaskConical,
  GitBranch,
  Github,
  MessageSquare,
} from "lucide-react";

const Labs = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Protocol", href: "/protocol" },
    { name: "Labs", href: "/labs", current: true },
    { name: "Docs", href: "https://docs.tinycloud.xyz", external: true },
    {
      name: "GitHub",
      href: "https://github.com/tinycloudlabs",
      external: true,
    },
  ];

  const tableOfContents = [
    { name: "Mission", href: "#mission" },
    { name: "Products", href: "#products" },
    { name: "Technologies", href: "#technologies" },
    { name: "Experiments", href: "#experiments" },
    { name: "Collaborations", href: "#collaborations" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="border-b border-[#4473b9]/30 bg-[#4473b9] backdrop-blur-md sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex">
              <div className="flex flex-shrink-0 items-center">
                <a href="/" className="flex items-center">
                  <img
                    src="/tinycloud-text-logo.png"
                    alt="TinyCloud"
                    className="h-8 w-auto object-contain"
                  />
                </a>
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    item.current
                      ? "border-b-2 border-white text-white"
                      : "text-white/90 hover:text-white hover:border-b-2 hover:border-white/50"
                  }`}
                >
                  {item.name}
                  {item.external && <ExternalLink className="ml-1 h-3 w-3" />}
                </a>
              ))}
            </div>
            <div className="sm:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-white/90 hover:bg-white/10 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden">
            <div className="space-y-1 pb-3 pt-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`block py-2 pl-3 pr-4 text-base font-medium ${
                    item.current
                      ? "bg-white/20 text-white"
                      : "text-white/90 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {item.name}
                  {item.external && (
                    <ExternalLink className="ml-1 h-3 w-3 inline" />
                  )}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Table of Contents - Desktop */}
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-24">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Contents
              </h3>
              <nav className="mt-4">
                <ul className="space-y-2">
                  {tableOfContents.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className="block text-sm text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9">
            <article className="prose prose-lg max-w-none">
              {/* Hero Section */}
              <div id="mission" className="mb-16">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  TinyCloud Labs
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Building the sovereign data layer that gives you control of
                  your digital self. Everything we explore furthers our mission
                  of a world where your data works for you and your interests,
                  not corporations, governments, or superintelligent AI.
                </p>
              </div>

              {/* Contact Section */}
              <section id="contact" className="mb-16">
                <div className="bg-[#4473b9]/5 border border-[#4473b9]/20 rounded-lg p-8">
                  <div className="flex items-start space-x-4">
                    <div className="p-2 bg-[#4473b9]/10 rounded-lg">
                      <MessageSquare className="h-6 w-6 text-[#4473b9]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Interested in Our Work?
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Want to learn more about what we're building or explore
                        opportunities to work together? Schedule a chat with
                        Sam, our founder.
                      </p>
                      <a
                        href="https://tinycloud.xyz/meet-sam"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-[#4473b9] text-white font-semibold rounded-lg hover:bg-[#4473b9]/90 transition-colors"
                      >
                        Chat with Sam
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Products Section */}
              <section id="products" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Products
                </h2>
                <p className="text-gray-600 mb-8">
                  Revenue drivers that solve specific problems for individuals.
                </p>

                <div className="grid gap-8">
                  {/* Muse */}
                  <div className="border border-gray-200 rounded-lg p-8 hover:border-[#4473b9] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">
                        Muse
                      </h3>
                      <div className="flex space-x-2">
                        <a
                          href="https://muse.tinycloud.xyz/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                        >
                          Web App <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                        <a
                          href="/muse"
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                        >
                          Mobile App
                        </a>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Muse is an AI assistant for capturing and retrieving your
                      memories and knowledge, seamlessly integrated with your
                      data. A work in progress, it's our entrypoint into the
                      consumer market. Our goal with Muse is to make an AI
                      product that is easy to use to understand everything about
                      your life.
                    </p>
                    <p className="text-gray-500 text-sm">
                      Muse uses a combination of traditional web2 technologies
                      and TinyCloud protocol to securely store and retrieve your
                      data.
                    </p>
                  </div>

                  {/* Wikiforge */}
                  <div className="border border-gray-200 rounded-lg p-8 hover:border-[#4473b9] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">
                        Wikiforge
                      </h3>
                      <a
                        href="https://autograph-web.pages.dev/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        Product Mockup <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Wikiforge is a tool to automatically build and maintain
                      Obsidian-styled, markdown knowledge graphs. Wikiforge is
                      built on top of AutoGraph, a technology developed by
                      TinyCloud Labs.
                    </p>
                    <p className="text-gray-500 text-sm">
                      These graphs function like powerful wikis, creating and
                      updating articles based on entities and relations
                      extracted from user submissions.
                    </p>
                  </div>
                </div>
              </section>

              {/* Technologies Section */}
              <section id="technologies" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Technologies
                </h2>
                <p className="text-gray-600 mb-8">
                  Novel capabilities that we've developed here at TinyCloud
                  Labs.
                </p>

                <div className="grid gap-8">
                  {/* TinyCloud Protocol */}
                  <div className="border border-gray-200 rounded-lg p-8 hover:border-[#4473b9] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">
                        TinyCloud Protocol
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href="/litepaper"
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                        >
                          Litepaper
                        </a>
                        <a
                          href="http://demo.tinycloud.xyz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                        >
                          Demo <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                        <a
                          href="https://github.com/tinycloudlabs/tinycloud-node"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                        >
                          GitHub <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                        <a
                          href="https://docs.tinycloud.xyz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                        >
                          SDK Docs <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      A decentralized protocol for storing and computing on
                      sovereign data, enabling truly user-controlled cloud
                      services. The protocol lets users run their own TinyCloud
                      nodes, permission who has access via signing, and run
                      computations on their data via the protocol.
                    </p>
                  </div>

                  {/* Autograph */}
                  <div className="border border-gray-200 rounded-lg p-8 hover:border-[#4473b9] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">
                        Autograph
                      </h3>
                      <a
                        href="/autograph"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        Learn More
                      </a>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Autograph is a powerful tool for creating and maintaining
                      human-readable, markdown-based knowledge graphs. Built to
                      be compatible with TinyCloud and Obsidian, Autograph takes
                      in any unstructured data, identifies entities and
                      relations, and builds/maintains knowledge graphs from the
                      new data. Autograph is currently closed-source, and we are
                      making it available via API and the WikiForge product.
                    </p>
                  </div>
                </div>
              </section>

              {/* Experiments Section */}
              <section id="experiments" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Experiments
                </h2>
                <p className="text-gray-600 mb-8">
                  Explorations into new capabilities and technologies that
                  demonstrate the potential of the technologies we've built.
                </p>

                <div className="space-y-8">
                  {/* Location Experiments */}
                  <div className="border-l-4 border-[#4473b9] pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Location Experiments
                    </h3>
                    <blockquote className="text-gray-600 italic leading-relaxed">
                      Testing privacy-preserving location streaming and access
                      control over geospatial data. Building 3 apps to
                      demonstrate the capabilities of TinyCloud protocol.
                      Granting the ability to do a computation on your location,
                      instead of 0 or 100% of your location, enables a new class
                      of applications and services.
                    </blockquote>
                  </div>

                  {/* Private Set Intersection */}
                  <div className="border-l-4 border-[#4473b9] pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Private Set Intersection
                    </h3>
                    <div className="mb-3">
                      <a
                        href="https://psi-demo.pages.dev/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        Demo <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    </div>
                    <blockquote className="text-gray-600 italic leading-relaxed">
                      We are exploring the limits of personal data, given a set
                      of privacy-preserving technologies. Exploring
                      cryptographic protocols that allow two parties to compute
                      intersections of their datasets without revealing extra
                      information. Keeping this data private, adding additional
                      verifiable information, and being able to do computations
                      can enable verifiable, high-trust interactions (like
                      directly paying someone to advertise to them because they
                      verifiably match a criteria). Check out our{" "}
                      <a
                        href="https://psi-demo.pages.dev/"
                        className="text-[#4473b9] hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Never Have I Ever PSI Demo
                      </a>{" "}
                      to see a simple example of this in action.
                    </blockquote>
                  </div>

                  {/* Community Wiki Tools */}
                  <div className="border-l-4 border-[#4473b9] pl-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Community Wiki Tools
                    </h3>
                    <div className="mb-3 space-x-2">
                      <a
                        href="https://aleph.communitywiki.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        Aleph Demo <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                      <a
                        href="https://github.com/TinyCloudLabs/Aleph-Community-Wiki"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        GitHub <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    </div>
                    <blockquote className="text-gray-600 italic leading-relaxed">
                      The Community Wiki tool was an experiment to use Autograph
                      to build a community wiki. The goal of the wiki was to
                      provide insight into the ongoings of a popup community in
                      an interpretable way.
                    </blockquote>
                  </div>
                </div>
              </section>

              {/* Collaborations Section */}
              <section id="collaborations" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Collaborations
                </h2>
                <p className="text-gray-600 mb-8">
                  Partnerships and collaborations that extend TinyCloud's capabilities.
                </p>

                <div className="grid gap-8">
                  {/* Aqua */}
                  <div className="border border-gray-200 rounded-lg p-8 hover:border-[#4473b9] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">
                        Aqua
                      </h3>
                      <a
                        href="https://dev.inblock.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        Learn More <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Our partnership with Aqua explores combining Aqua's protocol with credentials from TinyCloud and OpenCredentials to enable legal document-based data access permissions. For example, a user signing an NDA through Aqua could then gain permissioned access to data secured by TinyCloud, bridging legal agreements with cryptographic access control.
                    </p>
                  </div>

                  {/* EigenCloud */}
                  <div className="border border-gray-200 rounded-lg p-8 hover:border-[#4473b9] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-2xl font-semibold text-gray-900">
                        EigenCloud
                      </h3>
                      <a
                        href="https://docs.eigencloud.xyz/products/eigencompute/eigencompute-overview"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm px-3 py-1 bg-[#4473b9]/10 text-[#4473b9] rounded-full hover:bg-[#4473b9]/20 transition-colors"
                      >
                        EigenCompute <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      We're using EigenCompute as infrastructure to run TinyCloud nodes in a trustless manner, helping bootstrap the decentralized network of TinyCloud node operators. This partnership enables users to run TinyCloud nodes without requiring full trust, which is essential for creating a robust decentralized network where individuals can operate nodes confidently.
                    </p>
                  </div>
                </div>
              </section>

              {/* Footer Links */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="space-x-4">
                    <a
                      href="/"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Home
                    </a>
                    <a
                      href="/protocol"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Protocol
                    </a>
                    <a
                      href="/litepaper"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Litepaper
                    </a>
                  </div>
                  <div className="space-x-4">
                    <a
                      href="/privacy"
                      className="hover:text-gray-700 transition-colors"
                    >
                      Privacy
                    </a>
                    <a
                      href="https://github.com/tinycloudlabs"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-gray-700 transition-colors"
                    >
                      GitHub
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Labs;
